// G-1e / G-1g / G-1h: the connect-form schema at work. One manifest `form`
// drives the server's create/update validation, the config vs credential split,
// the run-time re-validation of stored config, and the SPA form and its
// client checks. Messages come from the manifest, so both sides say the same.
//
// Pure; no runtime imports (check-imports). The SPA imports this module.
import { hostMatches } from './manifest.js';
import { assertAllowedHost, HostNotAllowedError } from './hosts.js';
export const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** An unquoted SQL identifier (warehouse, role); quoted identifiers are not accepted. */
export const SQL_IDENTIFIER_PATTERN = /^[A-Za-z_][A-Za-z0-9_$]{0,254}$/;
/** A form value is refused; the message is safe to show (it never echoes a secret). */
export class FormValueError extends Error {
    field;
    constructor(message, field) {
        super(message);
        this.field = field;
        this.name = 'FormValueError';
    }
}
export const formInput = (f) => f.input ?? (f.store === 'credential' ? 'secret' : 'text');
export const isFormFieldRequired = (f) => f.required !== false;
/** Comma-separated list input → items. */
export const splitFormList = (v) => v.split(',').map((x) => x.trim()).filter(Boolean);
/**
 * The manifest SSRF guard for a `customer_host` field, plus the field's path
 * and deny rules. Returns the normalized URL stored in config
 * (`https://host`, or `https://host/base` with `path: 'keep'`).
 */
export function normalizeCustomerHost(m, f, raw) {
    const message = f.message ?? `${f.label} is not an address GAC Pro can call.`;
    let u;
    try {
        u = assertAllowedHost(m, raw);
    }
    catch (err) {
        if (err instanceof HostNotAllowedError && err.reason === 'not a URL')
            throw new FormValueError(f.host?.urlMessage ?? message, f.key);
        throw new FormValueError(message, f.key);
    }
    const host = u.hostname.toLowerCase();
    if (u.search || u.hash)
        throw new FormValueError(message, f.key);
    if ((f.host?.path ?? 'none') === 'keep')
        return `https://${host}${u.pathname.replace(/\/+$/, '')}`;
    if (u.pathname.replace(/\/$/, '') !== '')
        throw new FormValueError(message, f.key);
    for (const d of f.host?.deny ?? [])
        if (hostMatches(host, d.pattern))
            throw new FormValueError(d.message, f.key);
    return `https://${host}`;
}
function singleIssue(m, f, v, side) {
    const message = f.message;
    if (formInput(f) === 'select')
        return f.options?.some((o) => o.value === v) ? undefined : message ?? `Choose a ${f.label.toLowerCase()}.`;
    if (f.min !== undefined && v.length < f.min)
        return f.tooShort ?? `${f.label} is too short.`;
    if (f.max !== undefined && v.length > f.max)
        return `${f.label} is too long.`;
    const check = f.validate;
    if (!check)
        return undefined;
    if (check === 'customer_host') {
        // The server checks (and normalizes) the host when it builds the config.
        if (side === 'server')
            return undefined;
        try {
            normalizeCustomerHost(m, f, v);
            return undefined;
        }
        catch (err) {
            return err.message;
        }
    }
    if (check === 'guid')
        return GUID_PATTERN.test(v) ? undefined : message ?? 'This must be a GUID.';
    if (check === 'identifier')
        return SQL_IDENTIFIER_PATTERN.test(v) ? undefined : message ?? 'Use the unquoted name.';
    return new RegExp(check.regex, check.flags).test(v) ? undefined : message ?? `${f.label} is not valid.`;
}
/**
 * The one rule set for a field. `client` gets the trimmed form value (a list
 * as its comma-separated text); `server` gets the request value as sent.
 */
export function formFieldIssue(m, f, value, side) {
    if (formInput(f) === 'list') {
        const items = Array.isArray(value) ? value.map(String) : typeof value === 'string' ? splitFormList(value) : [];
        if (items.length === 0 || (f.list?.min !== undefined && items.length < f.list.min))
            return isFormFieldRequired(f) ? `${f.label} is required.` : undefined;
        if (f.list && items.length > f.list.max)
            return f.list.tooMany;
        for (const item of items) {
            const issue = singleIssue(m, f, item, side);
            if (issue)
                return issue;
        }
        return undefined;
    }
    const v = typeof value === 'string' ? value : '';
    if (!v)
        return isFormFieldRequired(f) ? `${f.label} is required.` : undefined;
    return singleIssue(m, f, v, side);
}
/**
 * `form.together`: a set is all-or-nothing. Returns field key → message for the
 * blank members of a partly filled set. `kept` names credential fields whose
 * stored value is kept when left blank (an update), so they count as filled.
 */
/** A label mid-sentence: "Client secret" → "client secret", but "API", "ChatGPT" and "ID" keep their capitals. */
function inSentence(label) {
    return label.split(' ').map((w) => (/^[A-Z][a-z]*$/.test(w) ? w.toLowerCase() : w)).join(' ');
}
export function formTogetherIssues(form, values, kept = new Set()) {
    const errors = {};
    const filled = (k) => kept.has(k) || (typeof values[k] === 'string' ? values[k].trim() !== '' : values[k] != null);
    for (const set of form?.together ?? []) {
        const have = set.filter(filled);
        if (have.length === 0 || have.length === set.length)
            continue;
        const label = (k) => form.fields.find((f) => f.key === k)?.label ?? k;
        for (const k of set)
            if (!filled(k))
                errors[k] = `Add ${inSentence(label(k))} too, or clear ${have.map((h) => inSentence(label(h))).join(' and ')}.`;
    }
    return errors;
}
/** Client-side check of a whole form: field key → message. */
export function validateFormValues(m, values, kept) {
    const errors = {};
    for (const f of m.form?.fields ?? []) {
        const raw = values[f.key] ?? '';
        // A stored credential left blank on update is kept (the server fills it from the stored one).
        if (kept?.has(f.key) && !raw.trim())
            continue;
        const issue = formFieldIssue(m, f, f.trim === false ? raw : raw.trim(), 'client');
        if (issue)
            errors[f.key] = issue;
    }
    for (const [k, msg] of Object.entries(formTogetherIssues(m.form, values, kept)))
        errors[k] ??= msg;
    return errors;
}
/** Form values → the request body fields (lists split, trimmed unless `trim: false`, empty optional fields omitted). */
export function formRequestBody(form, values) {
    const body = {};
    for (const f of form.fields) {
        const raw = values[f.key] ?? '';
        if (formInput(f) === 'list') {
            const items = splitFormList(raw);
            if (items.length || isFormFieldRequired(f))
                body[f.key] = items;
            continue;
        }
        const v = f.trim === false ? raw : raw.trim();
        if (v || isFormFieldRequired(f))
            body[f.key] = v;
    }
    return body;
}
/**
 * Split a validated request into non-secret config and the credential to
 * seal, in field order. `customer_host` values are normalized (and refused
 * with a FormValueError). No credential value → `credential: null`.
 */
export function buildFormConnector(m, body) {
    const config = {};
    const credential = {};
    const apart = Object.entries(formTogetherIssues(m.form, body))[0];
    if (apart)
        throw new FormValueError(apart[1], apart[0]);
    for (const f of m.form?.fields ?? []) {
        let v = body[f.key];
        if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0 && !isFormFieldRequired(f)))
            continue;
        if (f.validate === 'customer_host')
            v = normalizeCustomerHost(m, f, v);
        if (f.store !== 'credential')
            config[f.key] = v;
        if (f.store !== 'config')
            credential[f.key] = String(v);
    }
    return { config, credential: Object.keys(credential).length ? credential : null };
}
/**
 * Run-time re-validation of stored config (SSRF on every run, not only at
 * create). Returns the config with `customer_host` values normalized.
 */
export function revalidateFormConfig(m, config) {
    const out = { ...config };
    for (const f of m.form?.fields ?? []) {
        if (f.store === 'credential')
            continue;
        const v = config[f.key];
        if (v === undefined || v === null || v === '') {
            if (isFormFieldRequired(f))
                throw new FormValueError(`${f.label} is missing; update the connection.`, f.key);
            continue;
        }
        if (f.validate === 'customer_host') {
            out[f.key] = normalizeCustomerHost(m, f, v);
            continue;
        }
        const issue = formFieldIssue(m, f, v, 'server');
        if (issue)
            throw new FormValueError(`${f.label} is not valid: ${issue}`, f.key);
    }
    return out;
}
/** True when every credential field is optional (e.g. a self-hosted server that may need no token). */
export function formCredentialOptional(form) {
    return form.fields.filter((f) => f.store !== 'config').every((f) => !isFormFieldRequired(f));
}
/** `{field.var}` placeholders in a hint link → the selected option's vars (default: the first option). */
export function resolveFormTemplate(form, template, values) {
    return template.replace(/\{([a-z0-9_]+)\.([a-z0-9_]+)\}/g, (whole, key, name) => {
        const f = form.fields.find((x) => x.key === key);
        const option = f?.options?.find((o) => o.value === values[key]) ?? f?.options?.[0];
        return option?.vars?.[name] ?? whole;
    });
}
/** The config key the connections list shows as a connection's target (G-1h). */
export function connectorTargetField(m) {
    return m.ui.target?.field ?? m.hosts.customerSupplied?.field ?? null;
}
/** A human-readable target per the manifest's `ui.target` (the raw value stays in the details disclosure). */
export function friendlyConnectorTarget(m, target) {
    const display = m.ui.target?.display ?? (m.hosts.customerSupplied ? 'host' : 'raw');
    const field = m.form?.fields.find((f) => f.key === connectorTargetField(m));
    if (display === 'host') {
        try {
            return new URL(target).host;
        }
        catch {
            return target;
        }
    }
    if (display === 'option')
        return field?.options?.find((o) => o.value === target)?.label ?? target;
    if (display === 'tail4') {
        // A value that fails the field's own check is shown as is.
        if (field && typeof field.validate === 'object' && !new RegExp(field.validate.regex, field.validate.flags).test(target))
            return target;
        return `${m.ui.target?.noun ?? 'Id'} …${target.slice(-4)}`;
    }
    return target;
}
//# sourceMappingURL=form.js.map